import React, { useEffect, useState } from 'react';
import { CTA_TYPE, CTA_SIZE, COMPONENT_MODES } from '../../../data/constants';
import CtaComponent from '../../../components/reusable-ui/cta';
import _, { cloneDeep } from 'lodash';
import CustomTextField from '../../../components/reusable-ui/textfield-box';
import {
  isEditMode,
  isViewMode,
} from '../../../helpers/catalog-helper';
import { allowEverything } from '../../../utils';
import { useDispatch } from 'react-redux';
import { fetchCommonData } from '../../../redux/slices';
import { METHODS } from '../../../data/api-method-constants';
import { ENDPOINT } from '../../../services/apiUrls';
import { toastSuccess } from '../../../utils/react-toaster';
import { transformDataFromArrToMap, transformDataFromMapToArr } from '.';
import SearchInputComponent from '../../../components/reusable-ui/search-input';
import SwitchComponent from '../../../components/reusable-ui/switch';
import { isFeatureAllowed } from '../../../helpers/common-helper';
import { ALL_FEATURE_TYPES_MAP } from '../../../data/user-features-constants';
import { LEDGER_CONFIG_COLS, LEDGER_CONFIG_COLS_CONFIG } from './xml-constants';

interface LedgerType {
  type: string;
  ledger: string;
}

type Props = {
  mode: string;
  setMode: (val: string) => void;
  setLedgerMapping: (data: Record<string, string>) => void;
  selectedReportType: string;
}

function getLedgerRow(typeName: any, ristaName: any, data: any, rowIndex: number) {
  return {
    [LEDGER_CONFIG_COLS.MASTER_KEY]: typeName,
    ristaData: ristaName,
    tallyData: data,
    rowIndex
  };
}

const LedgerConfig = (props: Props) => {
  const {mode, setMode, setLedgerMapping, selectedReportType} = props;
  const [configRows, setConfigRows] = useState<any>([]);
  const [allLedgers, setAllLedgers] = useState([]);
  const [filterData, setFilterData] = useState<any>({
    searchValue: "",
    results: [],
    showOnlyUnmapped: false
  });
  const dispatch = useDispatch();

  useEffect(() => {
    getLedgerConfig();
  }, [selectedReportType]);

  const onSearchInput = (searchStr: string) => {
    handleFitlerChange({searchStr});
  };

  function handleFitlerChange(config: {
    showOnlyUnmapped?: boolean;
    searchStr?: string;
  }) {
    const {
      searchStr = filterData.searchValue,
      showOnlyUnmapped = filterData.showOnlyUnmapped,
    } = config;
    let results: any = [];
    if (searchStr && searchStr.length >= 3 && !!configRows?.length) {
      for (let rowObj of configRows) {
        if (_.isEmpty(rowObj)) continue;
        let valuesToMatch = [
          rowObj[LEDGER_CONFIG_COLS.RISTA_DATA],
          rowObj[LEDGER_CONFIG_COLS.MASTER_KEY],
          rowObj[LEDGER_CONFIG_COLS.TALLY_DATA],
        ];
        let rowMatched: boolean = valuesToMatch.reduce(
          (ac: boolean, val: string) => {
            return (
              ac ||
              val
                .toString()
                .toLowerCase()
                .trim()
                .includes(searchStr.toLowerCase().trim())
            );
          },
          false
        );
        if (rowMatched) {
          results.push(rowObj);
        }
      }
    }
    // if search filter is not enabled taking all rows
    if (searchStr?.length < 3) {
      results = cloneDeep(configRows);
    }

    // filtering unmapped rows if flag is enabled
    if (showOnlyUnmapped) {
      results = results.filter((rowObj: any) => !rowObj.tallyData);
    }
    setFilterData({
      searchValue: searchStr,
      results,
      showOnlyUnmapped,
    });
  }

  const handleOnlyUnmappedSwtichToggle = () => {
    handleFitlerChange({showOnlyUnmapped: !filterData.showOnlyUnmapped});
  }

  function saveLedgerConfig() {
    let ledgerData = configRows.reduce(
      (obj: any, item: any) =>
        Object.assign(obj, { [item.ristaData]: item.tallyData }),
      {}
    );
    if (Object.values(ledgerData)?.length === 0) {
      saveCallback({ res: 'success' });
      return;
    }
    let payload = {
      ledgersMapping: transformDataFromMapToArr(ledgerData),
      isNewCode: true,
      reportType: selectedReportType
    };
    dispatch(
      fetchCommonData({
        method: METHODS.POST,
        body: payload,
        url: ENDPOINT.ACCOUNTING.ledgerConfig(selectedReportType),
        callback: saveCallback,
      })
    );
  }

  function getLedgerConfig() {
    dispatch(
      fetchCommonData({
        method: METHODS.GET,
        url: ENDPOINT.ACCOUNTING.ledgerConfig(selectedReportType),
        callback: handleLedgerConfig,
      })
    );
  }

  function saveCallback(response: any) {
    if (!response.code) {
      toastSuccess('Mapping Updated');
      let newMode = COMPONENT_MODES.viewMode;
      setMode(newMode);
      getLedgerConfig();
      handleFitlerChange({});
    }
  }

  function handleLedgerConfig(response: any) {
    if (response?.code) return;
    let LEDGER_COLUMNS = response.ledgers;
    let mappedData = transformDataFromArrToMap(response.ledgersMapping);
    setAllLedgers(LEDGER_COLUMNS);
    setLedgerMapping(mappedData);
    let newConfigRows = LEDGER_COLUMNS.map((value: any, rowIndex: number) =>
      getLedgerRow(value.type, value.ledger, mappedData[value.ledger] || '', rowIndex)
    );
    setConfigRows(newConfigRows);
  }

  function renderConfigHeader() {
    return (
      <div className="flex w-full bg-[#EDEDED] items-center border-black/[0.1] border-b-[1px]">
        {LEDGER_CONFIG_COLS_CONFIG.map((value: any, index: any) => {
          return (
            <div
              style={{ width: value.width }}
              className={`flex text-black font-medium text-14 } py-15 border-black/[0.1] ${
                value.key !== LEDGER_CONFIG_COLS.TALLY_DATA ? 'border-r-[1px]' : ''
              }`}
              key={index}
            >
              <div
                className={`${value?.key === LEDGER_CONFIG_COLS.MASTER_KEY ? 'pl-40' : 'mx-auto'}`}
              >
                {value.label}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderInputValue(rowIndex: any, headerValue: any, rowValue: any) {
    return isEditMode(mode) ? (
      <CustomTextField
        parentClass="w-full pl-40"
        placeholder="Enter value"
        regexCheck={allowEverything}
        value={rowValue[headerValue.key]}
        maxLength={50}
        customClass="pl-17"
        isDisabled={isViewMode(mode)}
        showErrorBox={false}
        handleChange={(value: any) => {
          handleRowUpdate(rowIndex, headerValue.key, value);
        }}
      />
    ) : (
      <div className="mx-auto">{rowValue[headerValue.key]}</div>
    );
  }

  function renderHR(){
    return <hr className='w-[3%] border-[#2A77F266]/40 border-[2px] '/>
  }

  function renderConfigRows(rowsToShow: Record<string, any>[]) {
    return (
      <div className="flex w-full flex-col h-[60vh] overflow-y-auto mb-24">
        {rowsToShow.map((rowValue: any, ind: any) => {
          let isUnmapped: boolean = !rowValue?.tallyData;
          let rowIndex = rowValue.rowIndex || ind;
          let ledgerName: LedgerType = allLedgers[rowIndex];
          return (
            <div className="flex  border-black/[0.1] border-r-[1px] border-b-[1px]" key={rowIndex}>
              {LEDGER_CONFIG_COLS_CONFIG.map((headerValue: any, index: any) => {
                return (
                  <div
                    className={`p-16 h-80 items-center flex border-black/[0.1] border-b-[1px] ${
                      headerValue?.getClassName
                        ? headerValue.getClassName(isUnmapped)
                        : headerValue?.className
                    } `}
                    style={{ width: headerValue.width }}
                    key={index}
                  >
                    {headerValue.key === LEDGER_CONFIG_COLS.TALLY_DATA && renderHR()}
                    {headerValue.key === LEDGER_CONFIG_COLS.MASTER_KEY ? (
                      <div className="my-auto">{ledgerName.type}</div>
                    ) : headerValue.key === LEDGER_CONFIG_COLS.RISTA_DATA ? (
                      <div className="my-auto mx-auto">{ledgerName.ledger}</div>
                    ) : (
                      renderInputValue(rowIndex, headerValue, rowValue)
                    )}
                    {headerValue.key === LEDGER_CONFIG_COLS.RISTA_DATA && renderHR()}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  function handleRowUpdate(index: any, key: any, value: any) {
    let oldValue = cloneDeep(configRows[index]);
    let newVal = { ...oldValue, [key]: value };
    let newRows = configRows.map((rowValue: any, rowIndex: any) => {
      if (rowIndex !== index) return rowValue;
      return newVal;
    });
    if (filterData.showOnlyUnmapped || filterData.searchValue?.length >= 3) {
      let newFilterResults = filterData.results.map((res: any) => {
        if (res.rowIndex === index) return newVal;
        return res;
      });
      setFilterData((prevState: any) => ({
        ...prevState,
        results: newFilterResults,
      }));
    }
    setConfigRows(newRows);
  }

  function toggleEdit() {
    if (isEditMode(mode)) saveLedgerConfig();
    else {
      let newMode = COMPONENT_MODES.editMode;
      setMode(newMode);
    }
  }

  function renderEditSaveButtons() {
    return (
      <div className="flex items-center">
        {/* {isViewMode(mode) && (
          <div
            onClick={props.toggleDelete}
            className="flex w-[40px] ml-auto cursor-pointer h-full items-center justify-center border border-[#397EF5] rounded-[8px]"
          >
            <div className="rista-sprite ic-delete-blue "></div>
          </div>
        )} */}
        <CtaComponent
          customClass={'ml-auto'}
          ctaSize={CTA_SIZE.medium}
          onCtaClick={toggleEdit}
          ctaType={
            isViewMode(mode)
              ? CTA_TYPE.secondary_active_cta
              : CTA_TYPE.active_cta
          }
          ctaText={isViewMode(mode) ? 'Edit Mapping' : 'Save Mapping'}
        />
      </div>
    );
  }

  function renderFilteredResults() {
    return filterData.results.length ? (
      renderConfigRows(filterData.results)
    ) : (
      <div className="my-12">No results found</div>
    );
  }

  return (
    <div className="flex px-[63px] flex-col">
      <div className="font-semibold font-18 text-[#404040]/70 mb-8">
        Ledger Configuration:
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center justify-between">
          <SearchInputComponent
            onSerachInput={onSearchInput}
            searchMinLength={3}
            placeholder="Search by supplier name"
            customClass=""
            elementWidthClass=""
            regexCheck={allowEverything}
          />
          <div className="flex items-center justify-between border border-black/10 h-42 rounded-[8px] px-16 ml-12">
            <p className="mr-12 text-black/70 font-medium">
              Show unmapped fields
            </p>
            <SwitchComponent
              isChecked={filterData.showOnlyUnmapped}
              inputProps={{ "aria-label": "ant design" }}
              onSwitchClick={handleOnlyUnmappedSwtichToggle}
            />
          </div>
        </div>
        {isFeatureAllowed(
          ALL_FEATURE_TYPES_MAP.ALLOW_EDIT_XML_LEDGER_MAPPING
        ) && renderEditSaveButtons()}
      </div>
      <div className="flex flex-col mt-20">
        {renderConfigHeader()}
        {filterData.searchValue.length >= 3 || filterData.showOnlyUnmapped
          ? renderFilteredResults()
          : renderConfigRows(configRows)}
      </div>
    </div>
  );
};
export default LedgerConfig;
